import axios, { AxiosRequestConfig } from 'axios'

import { clearAuthStorage, getAffiliateToken, setSessionExpiredFlag } from '@/utils/storage'

/**
 * Axios do Portal do Afiliado. Separado do `api` de gestão de propósito: o
 * interceptor de lá injeta `company_ids` e lê @ME:access_token — o afiliado não tem
 * empresa e o `affiliate-jwt` do backend só aceita o token de afiliado.
 */
const affiliateApi = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 60_000
})

export function attachAffiliateToken(config: AxiosRequestConfig): AxiosRequestConfig {
  const headers: Record<string, string> = { ...((config.headers as Record<string, string>) ?? {}) }
  delete headers.company_ids
  delete headers.Company_ids
  const token = getAffiliateToken()
  if (token) headers.Authorization = `Bearer ${token}`
  return { ...config, headers }
}

/**
 * Sem token de afiliado a chamada nem sai. Vira um erro com cara de 401 (a tela
 * mostra a mensagem padrão), mas NÃO limpa a sessão nem redireciona: o caso real é
 * uma corrida no "Voltar para a Gestão" (layout do afiliado desmontando com a sessão
 * de gestão viva) e, sem esta trava, o 401 do backend derrubaria o gestor. Quem não
 * tem sessão nenhuma já é barrado pelo guard das rotas do afiliado.
 */
export class AffiliateTokenMissingError extends Error {
  readonly response = { status: 401 }

  constructor() {
    super('Sessão do afiliado ausente.')
    this.name = 'AffiliateTokenMissingError'
  }
}

export const isAffiliateTokenMissingError = (error: unknown): error is AffiliateTokenMissingError =>
  error instanceof AffiliateTokenMissingError

export function requireAffiliateToken(config: AxiosRequestConfig): AxiosRequestConfig {
  if (!getAffiliateToken()) throw new AffiliateTokenMissingError()
  return attachAffiliateToken(config)
}

let isRedirectingOn401 = false

/** Só para testes: libera o redirect de novo. */
export function resetAffiliateRedirectGuard(): void {
  isRedirectingOn401 = false
}

export function handleAffiliateUnauthorized(error: unknown): Promise<never> {
  // Não houve requisição: nada a deslogar (ver AffiliateTokenMissingError).
  if (isAffiliateTokenMissingError(error)) return Promise.reject(error)

  const e = error as { response?: { status?: number; data?: { statusCode?: number } } }
  const isUnauthorized = e?.response?.status === 401 || e?.response?.data?.statusCode === 401

  if (isUnauthorized && !isRedirectingOn401) {
    isRedirectingOn401 = true
    clearAuthStorage()
    setSessionExpiredFlag()
    window.location.href = '/'
  }

  return Promise.reject(error)
}

affiliateApi.interceptors.request.use(requireAffiliateToken)
affiliateApi.interceptors.response.use((response) => response, handleAffiliateUnauthorized)

export default affiliateApi
